import type {
  BespokeTemplateResponse,
  FormSchemaVersion,
  FormSchemaVersionPin,
} from '../../services/api/types.gen';
import type { RogerDocFormType } from '../../services/api/models/RogerDocFormType';

/** Stable key for set membership / export allowlists. */
export function formSchemaVersionPinKey(pin: FormSchemaVersionPin): string {
  return `${pin.form_type}\0${pin.schema_version_name}\0${pin.form_schema_version}`;
}

/** Pin key from a bespoke template API row. */
export function formSchemaVersionPinKeyFromTemplate(
  template: Pick<
    BespokeTemplateResponse,
    'form_type' | 'schema_version_name' | 'form_schema_version'
  >
): string {
  return `${template.form_type}\0${template.schema_version_name}\0${template.form_schema_version}`;
}

/**
 * Wire pin from a bespoke template row.
 * `form_type` is a plain string on the API response; cast to the RogerDoc union.
 */
export function pinFromBespokeTemplate(
  template: Pick<
    BespokeTemplateResponse,
    'form_type' | 'schema_version_name' | 'form_schema_version'
  >
): FormSchemaVersionPin {
  return {
    form_type: template.form_type as RogerDocFormType,
    schema_version_name: template.schema_version_name,
    form_schema_version: template.form_schema_version,
  };
}

/** Human-readable pin for unresolved-destination messages. */
export function formSchemaVersionPinLabel(pin: FormSchemaVersionPin): string {
  return `${pin.form_type} · ${pin.schema_version_name} (${pin.form_schema_version})`;
}

/** Build a wire pin from a loaded `FormSchemaVersion` + its form type. */
export function pinFromFormSchemaVersion(
  formType: RogerDocFormType,
  version: FormSchemaVersion
): FormSchemaVersionPin | null {
  const form_schema_version = version.version?.trim();
  if (!form_schema_version) return null;
  return {
    form_type: formType,
    schema_version_name: version.name,
    form_schema_version,
  };
}

/** All wire pins for the versions listed for one form type. */
export function pinsFromFormSchemaVersions(
  formType: RogerDocFormType,
  versions: FormSchemaVersion[]
): FormSchemaVersionPin[] {
  return versions
    .map(version => pinFromFormSchemaVersion(formType, version))
    .filter((pin): pin is FormSchemaVersionPin => pin != null);
}

export function bespokeTemplateMatchesPin(
  template: BespokeTemplateResponse,
  pin: FormSchemaVersionPin
): boolean {
  return (
    template.form_type === pin.form_type &&
    template.schema_version_name.trim() === pin.schema_version_name.trim() &&
    template.form_schema_version === pin.form_schema_version
  );
}

/** Bespoke rows for one schema version pin. */
export function filterBespokeTemplatesForPin(
  templates: readonly BespokeTemplateResponse[],
  pin: FormSchemaVersionPin
): BespokeTemplateResponse[] {
  return templates.filter(
    t => t.section_name && bespokeTemplateMatchesPin(t, pin)
  );
}

/** Live template per section for a pin (validation uses live only). */
export function liveTemplatesBySectionForPin(
  templates: readonly BespokeTemplateResponse[],
  pin: FormSchemaVersionPin
): Map<string, BespokeTemplateResponse> {
  const map = new Map<string, BespokeTemplateResponse>();
  for (const template of templates) {
    if (
      !bespokeTemplateMatchesPin(template, pin) ||
      !template.section_name ||
      !template.layout_json ||
      template.status !== 'live'
    ) {
      continue;
    }
    map.set(template.section_name, template);
  }
  return map;
}

/** Primary Live or Paused template per section (Live wins) for a pin. */
export function enabledTemplatesBySectionForPin(
  templates: readonly BespokeTemplateResponse[],
  pin: FormSchemaVersionPin
): Map<string, BespokeTemplateResponse> {
  const bySection = new Map<string, BespokeTemplateResponse[]>();
  for (const template of templates) {
    if (
      !bespokeTemplateMatchesPin(template, pin) ||
      !template.section_name ||
      !template.layout_json ||
      (template.status !== 'live' && template.status !== 'paused')
    ) {
      continue;
    }
    const rows = bySection.get(template.section_name) ?? [];
    rows.push(template);
    bySection.set(template.section_name, rows);
  }

  const map = new Map<string, BespokeTemplateResponse>();
  for (const [sectionName, rows] of bySection) {
    const live = rows.find(t => t.status === 'live');
    const primary = live ?? rows[0];
    if (primary) {
      map.set(sectionName, primary);
    }
  }
  return map;
}
